// Key codes deliberately match the existing input/level-trigger and replay system.
export const key = (code, modifiers = {}) => ({code, ...modifiers});
const action = (id, label, primary, alternate = null, extra = {}) => ({id, label, defaults: [primary, alternate], ...extra});
const ctrl = code => key(code, {ctrl: true});

export const gameActions = [
    action('up', 'Accelerate / move up', key(38), key(87)),
    action('down', 'Brake / move down', key(40), key(83)),
    action('left', 'Lean / move left', key(37), key(65)),
    action('right', 'Lean / move right', key(39), key(68)),
    action('flip', 'Flip direction / retry after crash', key(32)),
    action('eject', 'Eject from vehicle', key(90)),
    action('jump', 'Jump (hold to charge)', key(88), key(16)),
    action('grab', 'Grab (hold)', key(70)),
    action('hat', 'Hat ability', key(69)),
    action('pause', 'Pause / resume', key(27), key(80)),
    action('pauseMenu', 'Toggle pause menu', key(9)),
    action('retry', 'Retry / pause', key(82)),
    action('restart', 'Restart from the beginning', key(82, {shift: true})),
    action('chat', 'Multiplayer chat', key(13)),
    action('test', 'Start / stop editor test', key(84)),
].map(value => ({...value, labelKey: `controls_game_${value.id}`}));

// The editor adapter dispatches these canonical shortcuts to the existing editor.
// Keeping transformations there preserves selection, snapping and undo behavior.
export const editorActions = [
    action('test', 'Test level', key(84)),
    action('deselect', 'Deselect / cancel tool', key(27)),
    action('copy', 'Copy', ctrl(67)),
    action('paste', 'Paste copied selection', key(86, {ctrl: true, shift: true})),
    action('clone', 'Duplicate at cursor', key(67)),
    action('duplicate', 'Duplicate in place', ctrl(68)),
    action('cut', 'Cut', ctrl(88)),
    action('undo', 'Undo', ctrl(90)),
    action('redo', 'Redo', ctrl(89)),
    action('selectAll', 'Select all', ctrl(65)),
    action('animate', 'Animate selected graphic', key(65, {ctrl: true, alt: true})),
    action('group', 'Group / ungroup', ctrl(71)),
    action('joint', 'Attach joint', ctrl(74)),
    action('delete', 'Delete selection / vertex', key(46), key(8)),
    action('rotateLeft', 'Rotate left', key(90), null, {shiftable: true}),
    action('rotateRight', 'Rotate right', key(88), null, {shiftable: true}),
    action('moveUp', 'Move selection up', key(38), null, {shiftable: true}),
    action('moveDown', 'Move selection down', key(40), null, {shiftable: true}),
    action('moveLeft', 'Move selection left', key(37), null, {shiftable: true}),
    action('moveRight', 'Move selection right', key(39), null, {shiftable: true}),
    action('depthUp', 'Move forward a layer', ctrl(38), null, {shiftable: true}),
    action('depthDown', 'Move backward a layer', ctrl(40), null, {shiftable: true}),
    action('wider', 'Increase width', key(68), null, {shiftable: true}),
    action('narrower', 'Decrease width', key(65), null, {shiftable: true}),
    action('taller', 'Increase height', key(87), null, {shiftable: true}),
    action('shorter', 'Decrease height', key(83), null, {shiftable: true}),
    action('pan', 'Pan camera / double-tap to find player', key(32)),
    action('shift', 'Larger steps / constrain (hold)', key(16)),
    action('control', 'Selection modifier (hold)', key(17)),
    action('alt', 'Alternate transform (hold)', key(18)),
    action('zoomIn', 'Zoom in', key(187), key(107), {shiftable: true}),
    action('zoomOut', 'Zoom out', key(189), key(109)),
    action('assets', 'Toggle asset browser', key(112)),
    action('overlays', 'Toggle joints / triggers', key(9)),
    ...['Select', 'Geometry', 'Polygon', 'Pen', 'Joints', 'Specials', 'Text', 'Art', 'Triggers', 'Settings'].map((label, i) =>
        action(`tool${i}`, `${label} tool`, key(i === 9 ? 48 : 49 + i), key(i === 9 ? 96 : 97 + i))),
].map(value => ({...value, labelKey: `controls_editor_${value.id}`}));

// W3C standard mapping: https://w3c.github.io/gamepad/#remapping
export const gamepadActions = gameActions.map(({id, label, labelKey}) => ({...action(id, label, ...({
    up: ['axis:1:-', 'button:12'], down: ['axis:1:+', 'button:13'],
    left: ['axis:0:-', 'button:14'], right: ['axis:0:+', 'button:15'],
    flip: ['button:0'], eject: ['button:1'], jump: ['button:5'], grab: ['button:2'], hat: ['button:3'],
    pause: ['button:9'], pauseMenu: [null], retry: ['button:8'], restart: ['button:6'],
    chat: [null], test: ['button:4'],
}[id])), labelKey}));
export const actionGroups = {game: gameActions, editor: editorActions, gamepad: gamepadActions};

const codeAliases = {91: 17, 93: 17, 224: 17, 61: 187, 171: 187, 173: 189};

export function normalizeCode(code) {
    return codeAliases[code] || code;
}

export function bindingFromEvent(event) {
    const code = normalizeCode(event.keyCode || event.which);
    if (!code || code === 229) return null;
    return key(code, {
        ctrl: code !== 17 && Boolean(event.ctrlKey || event.metaKey),
        shift: code !== 16 && Boolean(event.shiftKey),
        alt: code !== 18 && Boolean(event.altKey),
    });
}

export function bindingId(binding) {
    if (!binding || typeof binding === 'string') return binding;
    return `${normalizeCode(binding.code)}:${+!!binding.ctrl}:${+!!binding.shift}:${+!!binding.alt}`;
}

export function validBinding(binding, group) {
    if (binding === null) return true;
    if (group === 'gamepad') return typeof binding === 'string' && /^(button:\d{1,2}|axis:\d{1,2}:[+-])$/.test(binding);
    return binding && Number.isInteger(binding.code) && binding.code > 0 && binding.code < 256 &&
        ['ctrl', 'shift', 'alt'].every(mod => binding[mod] === undefined || typeof binding[mod] === 'boolean');
}

export class Bindings {
    constructor(saved) {
        this.values = {};
        for (const [group, actions] of Object.entries(actionGroups)) {
            this.values[group] = {};
            for (const action of actions) {
                const stored = saved?.[group]?.[action.id];
                this.values[group][action.id] = Array.isArray(stored) && stored.length === 2 && stored.every(b => validBinding(b, group))
                    ? structuredClone(stored) : structuredClone(action.defaults);
            }
        }
    }
    set(group, actionId, slot, binding) {
        if (!this.values[group]?.[actionId] || ![0, 1].includes(slot) || !validBinding(binding, group)) return [];
        const displaced = [];
        if (binding) {
            for (const [otherAction, slots] of Object.entries(this.values[group])) {
                slots.forEach((other, index) => {
                    if (bindingId(other) === bindingId(binding) && (otherAction !== actionId || index !== slot)) {
                        slots[index] = null;
                        displaced.push(otherAction);
                    }
                });
            }
        }
        this.values[group][actionId][slot] = binding;
        return displaced;
    }
    reset(group) {
        this.values[group] = new Bindings().values[group];
    }
    match(group, event) {
        const pressed = bindingFromEvent(event);
        if (!pressed) return null;
        if ([16, 17, 18].includes(pressed.code)) pressed.ctrl = pressed.shift = pressed.alt = false;
        const actions = actionGroups[group];
        const pressedId = bindingId(pressed);
        const exact = actions.find(action => this.values[group][action.id].some(b => b && bindingId(b) === pressedId));
        if (exact) return exact;
        // Holding Shift increases editor movement/rotation steps and Alt
        // transforms the selection around its shared centre, unless that exact
        // chord has been assigned to another action.
        if (!pressed.shift && !pressed.alt) return null;
        const plainId = bindingId({...pressed, shift: false, alt: false});
        return actions.find(action => action.shiftable && this.values[group][action.id].some(b => b && bindingId(b) === plainId)) || null;
    }
}

const keyNames = {8: 'Backspace', 9: 'Tab', 13: 'Enter', 16: 'Shift', 17: 'Ctrl / Cmd', 18: 'Alt', 19: 'Pause', 20: 'Caps Lock',
    27: 'Esc', 32: 'Space', 33: 'Page Up', 34: 'Page Down', 35: 'End', 36: 'Home', 37: '←', 38: '↑', 39: '→', 40: '↓',
    45: 'Insert', 46: 'Delete', 106: 'Num *', 107: 'Num +', 109: 'Num −', 110: 'Num .', 111: 'Num /',
    144: 'Num Lock', 145: 'Scroll Lock', 186: ';', 187: '+ / =', 188: ',', 189: '−', 190: '.', 191: '/', 192: '`', 219: '[', 220: '\\', 221: ']', 222: "'"};
const buttonNames = ['A / Cross', 'B / Circle', 'X / Square', 'Y / Triangle', 'LB / L1', 'RB / R1', 'LT / L2', 'RT / R2',
    'View / Share', 'Start / Options', 'Left stick click', 'Right stick click', 'D-pad ↑', 'D-pad ↓', 'D-pad ←', 'D-pad →', 'Home'];
// Keep this input module independent of Settings/the UI. Callers can supply the
// existing localization lookup; standalone tools retain the English key labels.
export function bindingLabel(binding, standard = true, translate = (_id, fallback) => fallback) {
    if (!binding) return '—';
    if (typeof binding === 'string') {
        const [kind, indexText, direction] = binding.split(':');
        const index = Number(indexText);
        if (kind === 'button') {
            if (standard && buttonNames[index]) {
                if (index >= 12 && index <= 15) return translate('controls_pad_dpad', 'D-pad {direction}').replace('{direction}', ['↑', '↓', '←', '→'][index - 12]);
                return index >= 4 && index <= 7 ? buttonNames[index] : translate(`controls_pad_${index}`, buttonNames[index]);
            }
            return translate('controls_pad_button', 'Button {number}').replace('{number}', index + 1);
        }
        if (standard && index < 4) return translate(index < 2 ? 'controls_pad_left_stick' : 'controls_pad_right_stick',
            index < 2 ? 'Left stick {direction}' : 'Right stick {direction}').replace('{direction}', index % 2 ? (direction === '-' ? '↑' : '↓') : (direction === '-' ? '←' : '→'));
        return translate('controls_pad_axis', 'Axis {number} {direction}').replace('{number}', index + 1).replace('{direction}', direction);
    }
    const code = normalizeCode(binding.code);
    let name = keyNames[code] || (code >= 112 && code <= 135 ? `F${code - 111}` : String.fromCharCode(code));
    if ([8, 9, 13, 16, 17, 18, 19, 20, 27, 32, 33, 34, 35, 36, 45, 46, 144, 145].includes(code)) name = translate(`controls_key_${code}`, name);
    if (code >= 96 && code <= 111) {
        const numKey = code <= 105 ? String(code - 96) : keyNames[code]?.replace('Num ', '');
        if (numKey) name = translate('controls_key_numpad', 'Num {key}').replace('{key}', numKey);
    }
    return [binding.ctrl && translate('controls_key_17', 'Ctrl/Cmd'), binding.shift && translate('controls_key_16', 'Shift'),
        binding.alt && translate('controls_key_18', 'Alt'), name].filter(Boolean).join(' + ');
}
